import * as THREE from "three";
import { Sky } from "three/addons/objects/Sky.js";
import type { CelestialState } from "../time/AstronomyService";
import type { WeatherState } from "../weather/WeatherService";

export interface SkySnapshot {
  sun: { visible: boolean; altitudeDeg: number };
  moon: {
    visible: boolean;
    altitudeDeg: number;
    illuminatedFraction: number;
    phaseDeg: number;
  };
  starsOpacity: number;
  moonOpacity: number;
  cloudCoverage: number;
  directShadowEnabled: boolean;
}

export interface DerivedSkyState extends SkySnapshot {
  daylight: number;
  sunDiscOpacity: number;
  night: number;
  cloudDensity: number;
  fogDensity: number;
  directLightIntensity: number;
  ambientLightIntensity: number;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const unit = THREE.MathUtils.clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return unit * unit * (3 - 2 * unit);
}

export function deriveSkyState(celestial: CelestialState, weather: WeatherState): DerivedSkyState {
  const cloud = THREE.MathUtils.clamp(weather.cloud, 0, 1);
  const visibility = 1 - smoothstep(0.2, 0.9, cloud);
  const daylight = smoothstep(-6, 8, celestial.sun.altitudeDeg);
  const night = 1 - smoothstep(-18, -6, celestial.sun.altitudeDeg);
  const moonIllumination = THREE.MathUtils.clamp(celestial.moon.illuminatedFraction, 0, 1);

  return {
    sun: {
      visible: celestial.sun.visible,
      altitudeDeg: celestial.sun.altitudeDeg,
    },
    moon: {
      visible: celestial.moon.visible,
      altitudeDeg: celestial.moon.altitudeDeg,
      illuminatedFraction: celestial.moon.illuminatedFraction,
      phaseDeg: celestial.moon.phaseDeg,
    },
    daylight,
    sunDiscOpacity: smoothstep(-0.8, 0.2, celestial.sun.altitudeDeg),
    night,
    starsOpacity: night * visibility,
    moonOpacity:
      smoothstep(-1, 1, celestial.moon.altitudeDeg) *
      (0.25 + 0.75 * night) *
      (0.12 + 0.88 * Math.sqrt(moonIllumination)) *
      visibility,
    cloudCoverage: cloud,
    directShadowEnabled: 3.1 * daylight * (1 - 0.75 * cloud) > 0.01,
    cloudDensity: THREE.MathUtils.lerp(0.15, 0.9, cloud),
    fogDensity: 0.0035 + THREE.MathUtils.clamp(weather.fog, 0, 1) * 0.012,
    directLightIntensity: 3.1 * daylight * (1 - 0.75 * cloud),
    ambientLightIntensity: THREE.MathUtils.lerp(0.22, 1.8, daylight) * (1 - 0.3 * cloud),
  };
}

function randomFromSeed(seed: number): () => number {
  let value = seed;
  return () => {
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 0x1_0000_0000;
  };
}

const STAR_VERTEX_SHADER = /* glsl */ `
  attribute float brightness;
  attribute vec3 starColor;
  varying float vBrightness;
  varying vec3 vStarColor;

  void main() {
    vBrightness = brightness;
    vStarColor = starColor;
    gl_PointSize = 1.0 + 1.5 * brightness;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const STAR_FRAGMENT_SHADER = /* glsl */ `
  uniform float starsOpacity;
  varying float vBrightness;
  varying vec3 vStarColor;

  void main() {
    float pointAlpha = 1.0 - smoothstep(0.15, 0.5, distance(gl_PointCoord, vec2(0.5)));
    gl_FragColor = vec4(vStarColor, pointAlpha * vBrightness * starsOpacity);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

const MOON_VERTEX_SHADER = /* glsl */ `
  varying vec2 vMoonUv;

  void main() {
    vMoonUv = uv * 2.0 - 1.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const MOON_FRAGMENT_SHADER = /* glsl */ `
  uniform float phaseRadians;
  uniform float moonOpacity;
  varying vec2 vMoonUv;

  void main() {
    float radius = length(vMoonUv);
    if (radius > 1.0) discard;

    vec3 normal = vec3(vMoonUv, sqrt(max(0.0, 1.0 - radius * radius)));
    vec3 lightDirection = vec3(sin(phaseRadians), 0.0, -cos(phaseRadians));
    float normalDotLight = dot(normal, lightDirection);
    float derivative = max(fwidth(normalDotLight), 0.0001);
    float lit = smoothstep(-derivative, derivative, normalDotLight);
    float edge = 1.0 - smoothstep(1.0 - fwidth(radius), 1.0, radius);
    vec3 color = vec3(1.0, 0.96, 0.86) * mix(0.08, 1.0, lit);
    gl_FragColor = vec4(color, edge * moonOpacity);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

export class SkySystem {
  readonly #scene: THREE.Scene;
  readonly #camera: THREE.PerspectiveCamera;
  readonly #fog: THREE.FogExp2;
  readonly #skyRoot = new THREE.Group();
  readonly #lightRoot = new THREE.Group();
  readonly #sky = new Sky();
  readonly #stars: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>;
  readonly #moon: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  readonly #ambient = new THREE.HemisphereLight();
  readonly #direct = new THREE.DirectionalLight();
  readonly #sunDirection = new THREE.Vector3(0, 1, 0);
  readonly #moonDirection = new THREE.Vector3(0, 1, 0);
  readonly #fogColor = new THREE.Color();
  readonly #fogNight = new THREE.Color(0x08121c);
  readonly #fogDay = new THREE.Color(0x9ab6c2);
  readonly #fogOvercast = new THREE.Color(0x718087);
  readonly #ambientSkyColor = new THREE.Color();
  readonly #ambientSkyNight = new THREE.Color(0x172334);
  readonly #ambientSkyDay = new THREE.Color(0x91b9c7);
  readonly #ambientSkyOvercast = new THREE.Color(0x78868b);
  readonly #ambientGroundColor = new THREE.Color();
  readonly #ambientGroundNight = new THREE.Color(0x0d1214);
  readonly #ambientGroundDay = new THREE.Color(0x344038);
  readonly #directColor = new THREE.Color();
  readonly #directLow = new THREE.Color(0xff9a6a);
  readonly #directHigh = new THREE.Color(0xfff1d2);
  #state: DerivedSkyState = {
    sun: { visible: false, altitudeDeg: -90 },
    moon: { visible: false, altitudeDeg: -90, illuminatedFraction: 0, phaseDeg: 0 },
    daylight: 0,
    sunDiscOpacity: 0,
    night: 1,
    starsOpacity: 1,
    moonOpacity: 0,
    cloudCoverage: 0,
    directShadowEnabled: false,
    cloudDensity: 0.15,
    fogDensity: 0.0035,
    directLightIntensity: 0,
    ambientLightIntensity: 0.22,
  };

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
    if (!(scene.fog instanceof THREE.FogExp2)) {
      throw new Error("SkySystem requires THREE.FogExp2 scene fog");
    }
    this.#scene = scene;
    this.#camera = camera;
    this.#fog = scene.fog;

    this.#sky.name = "Atmospheric sky";
    this.#sky.scale.setScalar(3500);
    this.#sky.frustumCulled = false;
    const skyUniforms = this.#sky.material.uniforms;
    skyUniforms.mieDirectionalG!.value = 0.8;
    skyUniforms.cloudScale!.value = 0.0002;
    skyUniforms.cloudSpeed!.value = 0.00001;
    skyUniforms.cloudElevation!.value = 0.5;
    skyUniforms.nightOpacity = { value: 1 };
    this.#sky.material.fragmentShader = this.#sky.material.fragmentShader
      .replace("uniform float time;", "uniform float time;\nuniform float nightOpacity;")
      .replace(
        "gl_FragColor = vec4( texColor, 1.0 );",
        "texColor += vec3( 0.003, 0.008, 0.018 ) * nightOpacity;\ngl_FragColor = vec4( texColor, 1.0 );",
      );
    this.#skyRoot.add(this.#sky);

    const starGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(1500 * 3);
    const brightness = new Float32Array(1500);
    const colors = new Float32Array(1500 * 3);
    const random = randomFromSeed(0x5a17c9);
    for (let index = 0; index < 1500; index += 1) {
      const y = THREE.MathUtils.lerp(0.03, 1, random());
      const azimuth = random() * Math.PI * 2;
      const horizontal = Math.sqrt(1 - y * y);
      const offset = index * 3;
      positions[offset] = horizontal * Math.sin(azimuth) * 3000;
      positions[offset + 1] = y * 3000;
      positions[offset + 2] = -horizontal * Math.cos(azimuth) * 3000;
      brightness[index] = Math.pow(random(), 3);
      const colorRoll = random();
      if (colorRoll < 0.7) {
        colors[offset] = 0.95;
        colors[offset + 1] = 0.97;
        colors[offset + 2] = 1;
      } else if (colorRoll < 0.9) {
        colors[offset] = 0.72;
        colors[offset + 1] = 0.82;
        colors[offset + 2] = 1;
      } else {
        colors[offset] = 1;
        colors[offset + 1] = 0.78;
        colors[offset + 2] = 0.58;
      }
    }
    starGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    starGeometry.setAttribute("brightness", new THREE.BufferAttribute(brightness, 1));
    starGeometry.setAttribute("starColor", new THREE.BufferAttribute(colors, 3));
    const starMaterial = new THREE.ShaderMaterial({
      uniforms: { starsOpacity: { value: 1 } },
      vertexShader: STAR_VERTEX_SHADER,
      fragmentShader: STAR_FRAGMENT_SHADER,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.#stars = new THREE.Points(starGeometry, starMaterial);
    this.#stars.name = "Deterministic stars";
    this.#skyRoot.add(this.#stars);

    const moonSize = 2 * 2800 * Math.tan(THREE.MathUtils.degToRad(0.52 / 2));
    const moonGeometry = new THREE.PlaneGeometry(moonSize, moonSize);
    const moonMaterial = new THREE.ShaderMaterial({
      uniforms: {
        phaseRadians: { value: 0 },
        moonOpacity: { value: 0 },
      },
      vertexShader: MOON_VERTEX_SHADER,
      fragmentShader: MOON_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.#moon = new THREE.Mesh(moonGeometry, moonMaterial);
    this.#moon.name = "Phased moon";
    this.#skyRoot.add(this.#moon);

    this.#ambient.name = "Sky ambient light";
    this.#direct.name = "Astronomical sun light";
    this.#direct.castShadow = false;
    this.#direct.shadow.camera.left = -110;
    this.#direct.shadow.camera.right = 110;
    this.#direct.shadow.camera.top = 110;
    this.#direct.shadow.camera.bottom = -110;
    this.#direct.shadow.mapSize.set(2048, 2048);
    this.#lightRoot.add(this.#ambient, this.#direct, this.#direct.target);

    scene.add(this.#skyRoot, this.#lightRoot);
  }

  update(celestial: CelestialState, weather: WeatherState): DerivedSkyState {
    const state = deriveSkyState(celestial, weather);
    this.#state = state;
    const cloud = state.cloudCoverage;
    const fog = THREE.MathUtils.clamp(weather.fog, 0, 1);
    const haze = Math.max(cloud, fog);
    const skyUniforms = this.#sky.material.uniforms;

    this.#sunDirection.fromArray(celestial.sun.direction);
    this.#moonDirection.fromArray(celestial.moon.direction);
    skyUniforms.sunPosition!.value.copy(this.#sunDirection).multiplyScalar(450_000);
    skyUniforms.showSunDisc!.value = state.sunDiscOpacity;
    skyUniforms.cloudCoverage!.value = state.cloudCoverage;
    skyUniforms.cloudDensity!.value = state.cloudDensity;
    skyUniforms.turbidity!.value = THREE.MathUtils.lerp(3, 10, haze);
    skyUniforms.rayleigh!.value = THREE.MathUtils.lerp(2, 0.8, cloud);
    skyUniforms.mieCoefficient!.value = THREE.MathUtils.lerp(0.003, 0.012, haze);
    skyUniforms.nightOpacity!.value = state.night;

    this.#stars.material.uniforms.starsOpacity!.value = state.starsOpacity;
    this.#moon.position.copy(this.#moonDirection).multiplyScalar(2800);
    this.#moon.material.uniforms.phaseRadians!.value = THREE.MathUtils.degToRad(
      celestial.moon.phaseDeg,
    );
    this.#moon.material.uniforms.moonOpacity!.value = state.moonOpacity;

    this.#fogColor.copy(this.#fogNight).lerp(this.#fogDay, state.daylight);
    this.#fogColor.lerp(this.#fogOvercast, cloud * 0.55);
    this.#fog.color.copy(this.#fogColor);
    this.#fog.density = state.fogDensity;

    this.#ambientSkyColor.copy(this.#ambientSkyNight).lerp(this.#ambientSkyDay, state.daylight);
    this.#ambientSkyColor.lerp(this.#ambientSkyOvercast, cloud * 0.6);
    this.#ambient.color.copy(this.#ambientSkyColor);
    this.#ambientGroundColor
      .copy(this.#ambientGroundNight)
      .lerp(this.#ambientGroundDay, state.daylight);
    this.#ambient.groundColor.copy(this.#ambientGroundColor);
    this.#ambient.intensity = state.ambientLightIntensity;

    this.#directColor
      .copy(this.#directLow)
      .lerp(this.#directHigh, smoothstep(0, 20, celestial.sun.altitudeDeg));
    this.#direct.color.copy(this.#directColor);
    this.#direct.intensity = state.directLightIntensity;
    if (state.directShadowEnabled && !this.#direct.castShadow)
      this.#direct.shadow.needsUpdate = true;
    this.#direct.castShadow = state.directShadowEnabled;
    return state;
  }

  frameUpdate(deltaSeconds: number): void {
    this.#skyRoot.position.copy(this.#camera.position);
    this.#moon.quaternion.copy(this.#camera.quaternion);
    this.#direct.target.position.set(this.#camera.position.x, 0, this.#camera.position.z);
    this.#direct.position
      .copy(this.#direct.target.position)
      .addScaledVector(this.#sunDirection, 150);
    this.#sky.material.uniforms.time!.value += deltaSeconds;
  }

  snapshot(): SkySnapshot {
    return {
      sun: { ...this.#state.sun },
      moon: { ...this.#state.moon },
      starsOpacity: this.#state.starsOpacity,
      moonOpacity: this.#state.moonOpacity,
      cloudCoverage: this.#state.cloudCoverage,
      directShadowEnabled: this.#direct.castShadow,
    };
  }

  dispose(): void {
    this.#scene.remove(this.#skyRoot, this.#lightRoot);
    this.#sky.geometry.dispose();
    this.#sky.material.dispose();
    this.#stars.geometry.dispose();
    this.#stars.material.dispose();
    this.#moon.geometry.dispose();
    this.#moon.material.dispose();
    this.#direct.shadow.map?.dispose();
  }
}
